<template>
  <div class="camera-player">
    <div v-if="loading" class="player-state">
      <span class="spinner"></span>
      <span>Loading stream…</span>
    </div>
    <div v-else-if="error" class="player-state error">
      <span>Camera offline</span>
    </div>
    <video
      v-show="!loading && !error"
      ref="video"
      autoplay
      muted
      playsinline
      class="video-element"
    ></video>
  </div>
</template>

<script>
import Hls from 'hls.js'

export default {
  name: 'CameraPlayer',
  props: {
    playlistUrl: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      loading: true,
      error: false,
      hls: null,
      retryCount: 0,
      maxRetries: 5,
      retryTimer: null,
      nativeListeners: []
    }
  },
  watch: {
    playlistUrl() {
      this.loadStream()
    }
  },
  mounted() {
    this.loadStream()
  },
  beforeUnmount() {
    this.destroyPlayer()
  },
  methods: {
    loadStream() {
      this.destroyPlayer()
      this.loading = true
      this.error = false
      this.retryCount = 0

      const video = this.$refs.video
      if (!video) return

      if (video.canPlayType('application/vnd.apple.mpegurl')) {
        this.setupNativePlayer(video)
      } else if (Hls.isSupported()) {
        this.setupHlsPlayer(video)
      } else {
        this.loading = false
        this.error = true
      }
    },
    setupNativePlayer(video) {
      const load = () => {
        video.src = this.playlistUrl
        video.load()
      }

      const onLoaded = () => {
        this.loading = false
        this.retryCount = 0
        video.play().catch(() => {})
      }

      const onError = () => {
        if (this.retryCount >= this.maxRetries) {
          this.loading = false
          this.error = true
          return
        }
        this.retryCount++
        this.retryTimer = setTimeout(() => {
          if (!this.error) load()
        }, 2000)
      }

      video.addEventListener('loadedmetadata', onLoaded)
      video.addEventListener('error', onError)
      this.nativeListeners = [
        { event: 'loadedmetadata', handler: onLoaded },
        { event: 'error', handler: onError }
      ]

      load()
    },
    setupHlsPlayer(video) {
      this.hls = new Hls({
        manifestLoadingTimeOut: 10000,
        fragLoadingTimeOut: 20000,
        maxMaxBufferLength: 30
      })
      this.hls.loadSource(this.playlistUrl)
      this.hls.attachMedia(video)

      this.hls.on(Hls.Events.MANIFEST_PARSED, () => {
        this.loading = false
        this.retryCount = 0
        video.play().catch(() => {})
      })

      this.hls.on(Hls.Events.ERROR, (event, data) => {
        if (!data.fatal) return

        if (this.retryCount >= this.maxRetries) {
          this.loading = false
          this.error = true
          return
        }

        this.retryCount++

        if (data.type === Hls.ErrorTypes.NETWORK_ERROR) {
          this.retryTimer = setTimeout(() => {
            if (this.hls) {
              this.hls.startLoad()
            }
          }, 2000)
        } else if (data.type === Hls.ErrorTypes.MEDIA_ERROR) {
          this.retryTimer = setTimeout(() => {
            if (this.hls) {
              this.hls.recoverMediaError()
            }
          }, 1000)
        } else {
          this.loading = false
          this.error = true
        }
      })
    },
    destroyPlayer() {
      if (this.retryTimer) {
        clearTimeout(this.retryTimer)
        this.retryTimer = null
      }

      if (this.hls) {
        this.hls.destroy()
        this.hls = null
      }

      const video = this.$refs.video
      if (video) {
        this.nativeListeners.forEach(({ event, handler }) => {
          video.removeEventListener(event, handler)
        })
        this.nativeListeners = []
        video.pause()
        video.removeAttribute('src')
        video.load()
      }
    }
  }
}
</script>

<style scoped>
.camera-player {
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 9;
  background: var(--bg-tertiary);
  border-radius: var(--radius);
  overflow: hidden;
  border: 1px solid var(--border);
}

.video-element {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.player-state {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  color: var(--text-secondary);
  font-size: 0.95rem;
}

.player-state.error {
  color: var(--danger);
  background: var(--danger-bg);
}

.spinner {
  width: 18px;
  height: 18px;
  border: 2px solid var(--border-strong);
  border-top-color: var(--accent);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}
</style>
